import Phaser from 'phaser';
import { centreForFeet, feetForCentre } from './outdoorScale.mjs';
import { virtualInput } from './virtualInput.js';
import { nearStairBase } from './stairsMount.mjs';

// Climb-the-stairs-into-the-house mechanic, shared by MainScene (your house) and
// VisitMainScene (a friend's house) so both behave identically. Walking toward the
// house at the stair base mounts the stairs (UP, or RIGHT right at their foot); RIGHT/UP
// climbs a step, LEFT/DOWN descends; stepping past the top calls `onEnterHouse`.
//
// cfg: { natalka, walker, nodes, onEnterHouse, onMove?, onBroadcast?, stepMs? }
//   walker      : WalkController — disabled while on the stairs (it has an `enabled` flag)
//   nodes       : [[x, y], …] feet positions, base → landing (see mainLayout.stairNodesFor), or a function
//                 returning them (the house level, and with it the porch, can change while you stand there)
//   onEnterHouse: () => void | Promise — runs when climbing past the top step
//   onMove      : (x, y) => void — optional, called every frame as the avatar glides (the saved location)
//   onBroadcast : (move) => void — optional, the live move others see (realtime.move): sent like the walk
//                 controller sends it (~10 Hz, the 8-way facing, one more when she stops). Your own yard sent
//                 nothing, so others saw you stand at the foot until you went in; a visit sent a bare move every
//                 frame, which arrived in bunches and made the peer's walk stop and start (owner 2026-11-02).
export class StairsController {
  constructor(scene, { natalka, walker, nodes, onEnterHouse, onMove = null, onBroadcast = null, stepMs = 140 }) {
    this.scene = scene;
    this.natalka = natalka;
    this.walker = walker;
    this._nodes = nodes;
    this.onEnterHouse = onEnterHouse;
    this.onMove = onMove;
    this.onBroadcast = onBroadcast;
    this.stepMs = stepMs;
    this._lastBroadcast = 0;
    this._facing = 'right';

    const KC = Phaser.Input.Keyboard.KeyCodes;
    this.keys = scene.input.keyboard.addKeys({
      left: KC.LEFT, right: KC.RIGHT, up: KC.UP, down: KC.DOWN,
      A: KC.A, D: KC.D, W: KC.W, S: KC.S,
    });

    this._onStairs = false;
    this._index = 0;
    this._busy = false;
    this._entering = false;
  }

  get isOnStairs() { return this._onStairs; }
  get nodes() { return typeof this._nodes === 'function' ? this._nodes() : this._nodes; }

  // Call every frame while the avatar is on foot (not riding the bike).
  update() {
    if (this._onStairs) this._climb();
    else this._checkMount();
  }

  // Feet from the centre through the scene's scale/perspective (the yard draws her at 0.5×).
  _feet() { return { x: this.natalka.x, y: feetForCentre(this.scene, this.natalka.y) }; }
  _up()    { const k = this.keys; return k.up.isDown || k.W.isDown || virtualInput.isDown('up'); }
  _right() { const k = this.keys; return k.right.isDown || k.D.isDown; }
  _ascend()  { return this._right() || this._up(); }
  _descend() { const k = this.keys; return k.left.isDown  || k.A.isDown || k.down.isDown || k.S.isDown || virtualInput.isDown('down'); }

  // Which way the flight runs on screen (base → landing), as a unit-ish step: the stairs into the
  // house climb up and to the RIGHT, so ascending is up-right and descending is down-left.
  _axis() {
    const [bx, by] = this.nodes[0];
    const [tx, ty] = this.nodes[this.nodes.length - 1];
    return { dx: Math.sign(tx - bx), dy: Math.sign(ty - by) };
  }

  // Glide onto/within the stairs to a node (no hard snap → no teleport feel). `way` is +1 climbing,
  // -1 descending; it drives the walk animation, and the facing it leaves behind is the rest pose.
  // The walk keeps going from one step to the next: stopping it at each node restarted the cycle every
  // 140 ms, so the stairs showed its first frame over and over (owner 2026-10-31). _climb stops it
  // once no key carries her on.
  _tweenTo(index, way = +1) {
    this._index = index;
    this._busy = true;
    const [fx, fy] = this.nodes[index];
    const ty = centreForFeet(this.scene, fy);
    // The walk controller is off on the stairs, so the glide drives the walk animation itself. It
    // faces along the FLIGHT, not along this particular glide: stepping sideways onto the bottom
    // step still reads as "climbing", where the glide's own vector would face her up-left.
    const ax = this._axis();
    this.natalka.setMotion?.(ax.dx * way, ax.dy * way);
    if (ax.dx * way) this._facing = ax.dx * way < 0 ? 'left' : 'right';
    this.scene.tweens.add({
      targets: this.natalka, x: fx, y: ty, duration: this._glideMs(fx, ty),
      onUpdate: () => { this.onMove?.(this.natalka.x, this.natalka.y); this._broadcast(); },
      onComplete: () => { this._busy = false; },
    });
  }

  _broadcast(now = false) {
    if (!this.onBroadcast) return;
    const t = performance.now();
    if (!now && t - this._lastBroadcast < 100) return;        // ~10 Hz, as WalkController
    this._lastBroadcast = t;
    this.onBroadcast({ x: this.natalka.x, y: this.natalka.y, facing: this._facing, mode: 'walk', dir: this.natalka.facing8 });
  }

  // A glide takes as long as walking there would (WalkController's ground speed, depth counted at its 0.6), and at
  // least stepMs a step. A flat 140 ms pulled her onto the stairs from up to ~150 px away at ~1000 px/s (owner
  // 2026-10-31: "pulled too quickly").
  _glideMs(x, centreY) {
    const speed = this.walker?._currentSpeed?.() ?? 220;
    const ground = Math.hypot(x - this.natalka.x, (centreY - this.natalka.y) / 0.6);
    return Math.max(this.stepMs, (1000 * ground) / speed);
  }

  // On the ground: near the base, UP mounts the stairs from anywhere in the band, RIGHT
  // only when the feet are already at the foot of the steps. The path in front of the
  // house runs just BELOW the foot (the floor line follows the foundation), and walking
  // along it towards the backyard must not pull the child onto the stairs — it used to,
  // and the only way past was to hug the bottom of the screen.
  _checkMount() {
    if (this._busy) return;
    const f = this._feet();
    const atFoot = f.y <= this.nodes[0][1] + 14;
    if (nearStairBase(f, this.nodes[0]) && (this._up() || (this._right() && atFoot))) {
      this._onStairs = true;
      if (this.walker) this.walker.enabled = false;
      this._tweenTo(0, +1);   // glide to the base step instead of snapping, facing up the flight
    }
  }

  // On the stairs: ascend = up a step, descend = down a step (one step per stepMs while held).
  _climb() {
    if (this._busy || this._entering) return;
    if (this._ascend()) this._step(+1);
    else if (this._descend()) this._step(-1);
    else this._stand();
  }

  _stand() {
    const m = this.natalka._motion;
    if (m && m.dx === 0 && m.dy === 0) return;
    this.natalka.setMotion?.(0, 0);
    this._broadcast(true);                // where she stopped, so others don't stop a step short
  }

  _step(dir) {
    const next = this._index + dir;
    if (next < 0) {                       // off the bottom → back to free ground walking
      this._onStairs = false;
      this._stand();                      // the walk controller takes the motion from here
      if (this.walker) this.walker.enabled = true;
      return;
    }
    if (next >= this.nodes.length) {      // past the top → into the house
      this._enter();
      return;
    }
    this._tweenTo(next, dir);
  }

  async _enter() {
    if (this._entering) return;
    this._entering = true;
    this._stand();                        // not mid-stride while "open the house?" waits
    // Reset on return so a cancelled entry (e.g. "open the house?" declined) can retry.
    try { await this.onEnterHouse(); }
    finally { this._entering = false; }
  }
}
